import { decodeDrillDownFilterSelectionId } from '~/client/src/shared/components/DrillDownFilter/filterSelectionIds'

import { compareNaturalLabels } from './compareNaturalLabels'
import { IMapViewItem } from './MapViewsSidebarListItem'

export const FILTER_FACET_TYPE = 'type'
export const FILTER_FACET_SCOPE = 'scope'

export type SortKey =
  | 'default'
  | 'name'
  | 'type'
  | 'lastUpdated'
  | 'createdAt'
  | 'location'
export type SortDirection = 'asc' | 'desc'
export interface ISortState {
  key: SortKey
  direction: SortDirection
}
export type GroupKey = 'none' | 'scope' | 'lbs' | 'type'

// Ids encoded via `encodeDrillDownFilterSelectionId` so a scope label can't collide with capability.
export type FilterSelection = readonly string[]

export interface IScopeOption {
  id: string
  label: string
}

export interface IMapViewsTransformResult {
  items: IMapViewItem[]
  visibleCount: number
  /** Pre-filter items.length — drives "X of Y" counter. */
  totalCount: number
}

interface IFilterPredicate {
  capabilities: ReadonlySet<string>
  scopeLabels: ReadonlySet<string>
}

const decodeFilterSelection = (
  selection: FilterSelection,
): IFilterPredicate => {
  const capabilities = new Set<string>()
  const scopeLabels = new Set<string>()
  for (const id of selection) {
    const decoded = decodeDrillDownFilterSelectionId(id)
    if (!decoded) continue
    if (decoded.facetKey === FILTER_FACET_TYPE) {
      capabilities.add(decoded.optionId)
    } else if (decoded.facetKey === FILTER_FACET_SCOPE) {
      scopeLabels.add(decoded.optionId)
    }
  }
  return { capabilities, scopeLabels }
}

/** Canonical project-scoped predicate. Compares against data model (not translated label) so
 *  sort comparators, bucket keys, and header renderers can never disagree across locales. */
export const isProjectScopedMapView = (item: IMapViewItem): boolean =>
  item.lbsAncestry.length === 0

const compareForKey = (
  a: IMapViewItem,
  b: IMapViewItem,
  key: SortKey,
): number => {
  switch (key) {
    case 'default':
      // Stable sort returning 0 preserves authored mapIds order — the only order reorder can persist.
      return 0
    case 'name':
      return a.name.localeCompare(b.name)
    case 'lastUpdated':
      return a.updatedAt - b.updatedAt
    case 'createdAt':
      return a.createdAt - b.createdAt
    case 'type':
      // Sort on the discriminator (not the translated label) for locale-stable order.
      return a.capability.localeCompare(b.capability)
    case 'location':
      return compareNaturalLabels(a.scopeLabel, b.scopeLabel)
  }
}

const EMPTY_BUCKET_ANCESTORS: readonly string[] = []

/** Root-first ancestor list for an LBS bucket path.
 *    ''       → []
 *    '/a'     → ['']
 *    '/a/b/c' → ['', '/a', '/a/b']  */
export function getLbsBucketAncestors(path: string): readonly string[] {
  if (path === '') return EMPTY_BUCKET_ANCESTORS
  const ancestors: string[] = ['']
  let cursor = path.indexOf('/', 1)
  while (cursor > 0) {
    ancestors.push(path.slice(0, cursor))
    cursor = path.indexOf('/', cursor + 1)
  }
  return ancestors
}

/** Filter + search + sort. Grouping handled separately via `buildMapViewsGroupConfig`. */
export function applyMapViewsTransform(
  items: readonly IMapViewItem[],
  state: {
    sort: ISortState
    group: GroupKey
    filterSelection: FilterSelection
  },
  debouncedSearch: string,
): IMapViewsTransformResult {
  const totalCount = items.length
  const { capabilities, scopeLabels } = decodeFilterSelection(
    state.filterSelection,
  )

  let filtered: IMapViewItem[] = items as IMapViewItem[]
  if (capabilities.size > 0 || scopeLabels.size > 0) {
    filtered = filtered.filter(item => {
      if (capabilities.size > 0 && !capabilities.has(item.capability)) {
        return false
      }
      if (scopeLabels.size > 0 && !scopeLabels.has(item.scopeLabel)) {
        return false
      }
      return true
    })
  }

  // Search includes scopeLabel substring ("Building A" → maps tagged with Building A).
  const q = debouncedSearch.trim().toLowerCase()
  if (q) {
    filtered = filtered.filter(
      i =>
        i.name.toLowerCase().includes(q) ||
        i.scopeLabel.toLowerCase().includes(q),
    )
  }

  const sign = state.sort.direction === 'asc' ? 1 : -1
  const sorted = [...filtered].sort(
    (a, b) => sign * compareForKey(a, b, state.sort.key),
  )

  return {
    items: sorted,
    visibleCount: sorted.length,
    totalCount,
  }
}
